import React, { useState } from 'react';
import { 
  getConfidenceColor, 
  getConfidenceLabel, 
  formatFieldName,
  type ProgramVerification 
} from '../../services/rpc/verification';

interface FieldVerificationCardProps {
  verification: ProgramVerification;
  onUpdate: (
    verificationId: string,
    userAction: 'approve' | 'edit' | 'reject' | 'flag_learning',
    correctedValue?: any,
    correctionNotes?: string
  ) => void;
  isUpdating: boolean;
}

export const FieldVerificationCard: React.FC<FieldVerificationCardProps> = ({
  verification,
  onUpdate,
  isUpdating,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState(
    typeof verification.extractedValue === 'string' 
      ? verification.extractedValue.replace(/"/g, '') 
      : JSON.stringify(verification.extractedValue)
  );
  const [correctionNotes, setCorrectionNotes] = useState('');

  const handleEdit = () => {
    if (isEditing) {
      // Save changes
      let parsedValue;
      try {
        // Try to parse as JSON first
        parsedValue = JSON.parse(`"${editedValue}"`);
      } catch {
        // If that fails, use as string
        parsedValue = editedValue;
      }
      
      onUpdate(verification.id, 'edit', parsedValue, correctionNotes || undefined);
      setIsEditing(false);
      setCorrectionNotes('');
    } else {
      setIsEditing(true);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedValue(
      typeof verification.extractedValue === 'string' 
        ? verification.extractedValue.replace(/"/g, '') 
        : JSON.stringify(verification.extractedValue)
    );
    setCorrectionNotes('');
  };

  const displayValue = typeof verification.extractedValue === 'string' 
    ? verification.extractedValue.replace(/"/g, '') 
    : JSON.stringify(verification.extractedValue);

  const isApproved = verification.verificationStatus === 'approved';
  const isRejected = verification.verificationStatus === 'rejected';
  const isPending = verification.verificationStatus === 'pending_review';

  return (
    <div style={{
      border: `2px solid ${
        isApproved ? '#10b981' : 
        isRejected ? '#ef4444' : 
        '#e5e7eb'
      }`,
      borderRadius: '12px',
      backgroundColor: '#ffffff',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        backgroundColor: isApproved ? '#ecfdf5' : isRejected ? '#fef2f2' : '#f9fafb',
        borderBottom: '1px solid #e5e7eb',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h4 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#111827',
              margin: '0 0 4px 0',
            }}>
              {formatFieldName(verification.fieldName)}
            </h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: getConfidenceColor(verification.confidenceScore),
                }}
              />
              <span style={{ fontSize: '13px', color: '#6b7280' }}>
                {verification.confidenceScore.toFixed(1)}% confidence
              </span>
              <span style={{
                padding: '2px 6px',
                borderRadius: '8px',
                fontSize: '11px',
                fontWeight: '600',
                textTransform: 'uppercase',
                backgroundColor: getConfidenceColor(verification.confidenceScore) + '20',
                color: getConfidenceColor(verification.confidenceScore),
              }}>
                {getConfidenceLabel(verification.confidenceScore)}
              </span>
            </div>
          </div>
          
          {/* Status Badge */}
          <div style={{
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: '600',
            textTransform: 'uppercase',
            backgroundColor: 
              isApproved ? '#d1fae5' : 
              isRejected ? '#fee2e2' : 
              verification.verificationStatus === 'learning_flagged' ? '#fef3c7' :
              '#f3f4f6',
            color: 
              isApproved ? '#065f46' : 
              isRejected ? '#dc2626' : 
              verification.verificationStatus === 'learning_flagged' ? '#92400e' :
              '#6b7280',
          }}>
            {verification.verificationStatus.replace('_', ' ')}
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '16px' }}>
        {/* Extracted Value */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '4px',
          }}>
            Extracted Value:
          </label>
          {isEditing ? (
            <input
              type="text"
              value={editedValue}
              onChange={(e) => setEditedValue(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
          ) : (
            <div style={{
              padding: '8px 12px',
              backgroundColor: '#f9fafb',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'monospace',
              color: verification.extractedValue === null ? '#6b7280' : '#111827',
              fontStyle: verification.extractedValue === null ? 'italic' : 'normal',
            }}>
              {verification.extractedValue === null ? 'null (no data found)' : displayValue}
            </div>
          )}
        </div>

        {/* Source Information */}
        {verification.sourceSnippet && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '4px',
            }}>
              Source Text:
            </label>
            <div style={{
              padding: '8px 12px',
              backgroundColor: '#fffbeb',
              border: '1px solid #fcd34d',
              borderRadius: '6px',
              fontSize: '13px',
              color: '#92400e',
              fontStyle: 'italic',
              maxHeight: '80px',
              overflowY: 'auto',
            }}>
              "{verification.sourceSnippet}"
            </div>
            {verification.sourceUrl && (
              <div style={{ marginTop: '4px' }}>
                <a
                  href={verification.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: '12px',
                    color: '#3b82f6',
                    textDecoration: 'none',
                  }}
                >
                  📄 View Source Page
                </a>
              </div>
            )}
          </div>
        )}

        {/* Correction Notes (when editing) */}
        {isEditing && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '4px',
            }}>
              Correction Notes (optional):
            </label>
            <textarea
              value={correctionNotes}
              onChange={(e) => setCorrectionNotes(e.target.value)}
              placeholder="Explain why this correction was needed..."
              rows={2}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '13px',
                resize: 'vertical',
              }}
            />
          </div>
        )}

        {/* Previous Corrections */}
        {verification.correctedValue && !isEditing && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '500',
              color: '#065f46',
              marginBottom: '4px',
            }}>
              Corrected Value:
            </label>
            <div style={{
              padding: '8px 12px',
              backgroundColor: '#ecfdf5',
              border: '1px solid #6ee7b7',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'monospace',
              color: '#111827',
            }}>
              {typeof verification.correctedValue === 'string' 
                ? verification.correctedValue 
                : JSON.stringify(verification.correctedValue)}
            </div>
            {verification.correctionNotes && (
              <div style={{
                marginTop: '4px',
                fontSize: '12px',
                color: '#065f46',
                fontStyle: 'italic',
              }}>
                Note: {verification.correctionNotes}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {isPending && !isEditing && (
            <>
              <button
                onClick={() => onUpdate(verification.id, 'approve')}
                disabled={isUpdating}
                style={{
                  padding: '6px 12px',
                  borderRadius: '4px',
                  border: '1px solid #10b981',
                  backgroundColor: '#10b981',
                  color: '#ffffff',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: isUpdating ? 'not-allowed' : 'pointer',
                  opacity: isUpdating ? 0.6 : 1,
                }}
              >
                ✓ Approve
              </button>
              <button
                onClick={handleEdit}
                disabled={isUpdating}
                style={{
                  padding: '6px 12px',
                  borderRadius: '4px',
                  border: '1px solid #f59e0b',
                  backgroundColor: '#ffffff',
                  color: '#f59e0b',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: isUpdating ? 'not-allowed' : 'pointer',
                  opacity: isUpdating ? 0.6 : 1,
                }}
              >
                ✏️ Edit
              </button>
              <button
                onClick={() => onUpdate(verification.id, 'reject')}
                disabled={isUpdating}
                style={{
                  padding: '6px 12px',
                  borderRadius: '4px',
                  border: '1px solid #ef4444',
                  backgroundColor: '#ffffff',
                  color: '#ef4444',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: isUpdating ? 'not-allowed' : 'pointer',
                  opacity: isUpdating ? 0.6 : 1,
                }}
              >
                ✗ Reject
              </button>
              <button
                onClick={() => onUpdate(verification.id, 'flag_learning')}
                disabled={isUpdating}
                style={{
                  padding: '6px 12px',
                  borderRadius: '4px',
                  border: '1px solid #8b5cf6',
                  backgroundColor: '#ffffff',
                  color: '#8b5cf6',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: isUpdating ? 'not-allowed' : 'pointer',
                  opacity: isUpdating ? 0.6 : 1,
                }}
              >
                🎓 Flag for Learning
              </button>
            </>
          )}
          
          {isEditing && (
            <>
              <button
                onClick={handleEdit}
                disabled={isUpdating}
                style={{
                  padding: '6px 12px',
                  borderRadius: '4px',
                  border: '1px solid #10b981',
                  backgroundColor: '#10b981',
                  color: '#ffffff',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: isUpdating ? 'not-allowed' : 'pointer',
                  opacity: isUpdating ? 0.6 : 1,
                }}
              >
                💾 Save Changes
              </button>
              <button
                onClick={handleCancel}
                disabled={isUpdating}
                style={{
                  padding: '6px 12px',
                  borderRadius: '4px',
                  border: '1px solid #6b7280',
                  backgroundColor: '#ffffff',
                  color: '#6b7280',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: isUpdating ? 'not-allowed' : 'pointer',
                  opacity: isUpdating ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
            </>
          )}

          {verification.verifiedAt && (
            <div style={{
              marginLeft: 'auto',
              fontSize: '11px',
              color: '#6b7280',
              alignSelf: 'center',
            }}>
              {verification.userAction && `${verification.userAction} `}
              {new Date(verification.verifiedAt).toLocaleString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};